import type {
  CaseAttributeDto,
  CaseAttributeKind,
  CreateCaseAttributeInput,
  UpdateCaseAttributeInput,
} from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

/** Each kind has a path of its own; the two behave identically otherwise. */
const pathOf = (projectCode: string, kind: CaseAttributeKind) =>
  `/projects/${projectCode}/${kind === 'layer' ? 'layers' : 'modules'}`;

export const caseAttributesQueryOptions = (projectCode: string, kind: CaseAttributeKind) =>
  queryOptions({
    queryKey: queryKeys.caseAttributes.list(projectCode, kind),
    queryFn: ({ signal }) =>
      api.get<CaseAttributeDto[]>(pathOf(projectCode, kind), undefined, signal),
  });

/** `enabled` lets a closed dialog mount its picker without fetching. */
export const useCaseAttributes = (
  projectCode: string,
  kind: CaseAttributeKind,
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    ...caseAttributesQueryOptions(projectCode, kind),
    enabled: options.enabled ?? true,
  });

/** A layer or module is named inside test cases, so a rename changes those too. */
const useCaseAttributeInvalidation = (projectCode: string, kind: CaseAttributeKind) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.caseAttributes.list(projectCode, kind),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.testCases.all(projectCode) });
  };
};

export const useCreateCaseAttribute = (projectCode: string, kind: CaseAttributeKind) => {
  const invalidate = useCaseAttributeInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: (input: CreateCaseAttributeInput) =>
      api.post<CaseAttributeDto>(pathOf(projectCode, kind), input),
    onSuccess: invalidate,
  });
};

export const useUpdateCaseAttribute = (projectCode: string, kind: CaseAttributeKind) => {
  const invalidate = useCaseAttributeInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: ({ attributeId, input }: { attributeId: string; input: UpdateCaseAttributeInput }) =>
      api.patch<CaseAttributeDto>(`${pathOf(projectCode, kind)}/${attributeId}`, input),
    onSuccess: invalidate,
  });
};

export const useDeleteCaseAttribute = (projectCode: string, kind: CaseAttributeKind) => {
  const invalidate = useCaseAttributeInvalidation(projectCode, kind);
  return useMutation({
    mutationFn: (attributeId: string) =>
      api.delete(`${pathOf(projectCode, kind)}/${attributeId}`),
    onSuccess: invalidate,
  });
};
